import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { ArticleBatchDto } from 'src/app/shared/dto/article-batch.dto';
import { ArticleDto } from 'src/app/shared/dto/article.dto';
import { PackagingDto } from 'src/app/shared/dto/packaging.dto';
import { ArticleResponseDto } from 'src/app/shared/dto/pagination/articles-response.dto';
import { ArticleSearchResponseDto } from 'src/app/shared/dto/pagination/articles-search-response.dto';
import { RawMaterialDto } from 'src/app/shared/dto/rawMaterial.dto';
import { BaseService } from 'src/app/shared/services/base.service';
import { SocketService } from 'src/app/shared/services/socket.service';
import { Messages } from 'src/app/shared/sockets/enums/messages';
import { Client, formatClientRoom, RoomNames } from 'src/app/shared/sockets/enums/rooms';

@Injectable({
    providedIn: 'root',
})
export class ArticlesService extends BaseService {
    private articlesSubject = new BehaviorSubject<ArticleDto[]>([]);
    public articles$ = this.articlesSubject.asObservable();

    private readonly socketService = inject(SocketService);

    constructor(httpClient: HttpClient) {
        super(httpClient, 'articles');
    }

    async setupWebSocket(): Promise<void> {
        try {
            this.articlesSubject.next((await this.findAll()).articles);
            this.socketService.conectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.ARTICLES_ROOM));
            this.socketService.getNotifications(Messages.ARTICLES_CHANGED).subscribe(async () => {
                const response = await this.findAll();
                this.articlesSubject.next(response.articles);
            });
        } catch (e: any) {
            throw new Error(`Error al establecer conexión con el socket de Artículos. Error: ${e.message}`);
        }
    }

    disconnectWebSocket(): void {
        this.socketService.disconnectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.ARTICLES_ROOM));
    }

    async findAll(): Promise<ArticleResponseDto> {
        try {
            const request = this.httpClient.get<ArticleResponseDto>(`${this.url}/findAll`);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se ha encontrado ningun artículo. Error: ${e.message}`);
        }
    }

    async findAllWithArticleBatchesAndPackagings(article: string, familiesIds: number[], articleBatch: string, packagingBatch: string): Promise<ArticleSearchResponseDto[]> {
        try {
            const url: string = `${this.url}/findAllWithArticleBatchesAndPackagings?article=${article}&familiesIds=${familiesIds.toString()}&articleBatch=${articleBatch}&packagingBatch=${packagingBatch}`;

            const request = this.httpClient.get<ArticleSearchResponseDto[]>(`${url}`);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se ha encontrado ningun artículo. Error: ${e.message}`);
        }
    }

    async incrementQuantity(articleId: number, quantity: number): Promise<ArticleDto> {
        try {
            if (articleId <= 0) {
                throw new Error('El id del articulo ha de ser mayor de 0');
            }

            const request = this.httpClient.patch<ArticleDto>(`${this.url}/incrementArticleQuantity/${articleId}`, { quantity });

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se ha podido actualizar la cantidad del articulo ${articleId}: Error: ${e.message}`);
        }
    }

    async getArticleBatchByBatchNumber(batchNumber: string): Promise<ArticleBatchDto> {
        try {
            const url: string = `${this.url}/getArticleBatchByBatchNumber?batchNumber=${batchNumber}`;

            const request = this.httpClient.get<ArticleBatchDto>(`${url}`);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se ha encontrado ningun artículo. Error: ${e.message}`);
        }
    }

    async findArticleBatchByBatchNumberScan(batchNumber: string, articleId?: number): Promise<ArticleBatchDto> {
        try {
            let url: string = `${this.url}/findArticleBatchByBatchNumberScan?batchNumber=${batchNumber}`;

            if (articleId !== undefined && articleId !== null) {
                url += `&articleId=${articleId}`;
            }

            const request = this.httpClient.get<ArticleBatchDto>(url);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(e.error?.error || 'No se encontró el lote del artículo');
        }
    }

    async findAlternativeArticles(articleId: number): Promise<ArticleDto[]> {
        try {
            if (articleId <= 0) {
                throw new Error('El id del artículo ha de ser mayor de 0');
            }

            const request = this.httpClient.get<ArticleDto[]>(`${this.url}/findAlternativeArticles/${articleId}`);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se han podido obtener los artículos alternativos para el artículo ${articleId}. Error: ${e.error?.error || e.message}`);
        }
    }

    async requestLotFromVelneo(input: string): Promise<{ success: boolean; searchType?: string; article?: any; articleBatch?: any; articles?: any[]; articleBatches?: any[]; error?: string }> {
        try {
            const url = `${this.url}/requestLotFromVelneo?input=${input}`;
            const request = this.httpClient.get<{ success: boolean; searchType?: string; article?: any; articleBatch?: any; articles?: any[]; articleBatches?: any[]; error?: string }>(url);
            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(e.error?.error || 'Error al solicitar datos a Velneo');
        }
    }
}
